import type {
  CaptionCue,
  CourseManifest,
  CourseModule,
  CourseModuleRecord,
  CourseSlide,
  ModuleSummary,
} from "./types";

export const MANIFEST_URL = "/course/media-manifest.json";

const REQUIRED_SLIDE_FIELDS: Array<keyof CourseSlide> = [
  "id",
  "title",
  "module",
  "image",
  "audio",
  "transcript",
  "caption",
  "durationMs",
  "timelineStartMs",
  "timelineEndMs",
  "altText",
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isModuleRecord = (module: CourseModule): module is CourseModuleRecord =>
  typeof module === "object" && module !== null;

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

export function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }

  const wholeSeconds = Math.floor(seconds);
  const hours = Math.floor(wholeSeconds / 3600);
  const minutes = Math.floor((wholeSeconds % 3600) / 60);
  const remainingSeconds = wholeSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds,
    ).padStart(2, "0")}`;
  }

  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

export function formatDuration(milliseconds: number): string {
  const totalMinutes = Math.max(1, Math.round(milliseconds / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${totalMinutes} min`;
  }

  return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;
}

export function moduleLabel(module: CourseModule): string {
  if (typeof module === "string") {
    return module;
  }

  return (
    module.name ??
    module.title ??
    module.label ??
    (module.id === undefined ? "Course module" : String(module.id))
  );
}

export function slideModuleLabel(
  slide: CourseSlide,
  modules: CourseModule[],
): string {
  const matchingModule = modules.find((module) => {
    if (!isModuleRecord(module)) {
      return module === slide.module;
    }

    const identifiers = [
      module.id === undefined ? "" : String(module.id),
      module.name ?? "",
      module.title ?? "",
      module.label ?? "",
    ];
    const explicitSlides = module.slideIds ?? module.slides ?? [];

    return identifiers.includes(slide.module) || explicitSlides.includes(slide.id);
  });

  return matchingModule ? moduleLabel(matchingModule) : slide.module;
}

export function buildModuleSummaries(
  manifest: CourseManifest,
): ModuleSummary[] {
  const summaries: ModuleSummary[] = manifest.modules.map((module) => {
    const name = moduleLabel(module);
    const explicitSlides =
      typeof module === "string" ? [] : module.slideIds ?? module.slides ?? [];
    const rangeSlides =
      typeof module !== "string" &&
      typeof module.slideStart === "number" &&
      typeof module.slideEnd === "number"
        ? manifest.slides
            .filter(
              (slide) =>
                slide.id >= module.slideStart! && slide.id <= module.slideEnd!,
            )
            .map((slide) => slide.id)
        : [];
    const matchedSlides = manifest.slides
      .filter(
        (slide) =>
          slideModuleLabel(slide, manifest.modules) === name ||
          explicitSlides.includes(slide.id),
      )
      .map((slide) => slide.id);

    return {
      name,
      slideIds: [
        ...new Set([...explicitSlides, ...rangeSlides, ...matchedSlides]),
      ].sort((left, right) => left - right),
    };
  });

  for (const slide of manifest.slides) {
    const name = slideModuleLabel(slide, manifest.modules);
    const existing = summaries.find((summary) => summary.name === name);

    if (existing) {
      if (!existing.slideIds.includes(slide.id)) {
        existing.slideIds.push(slide.id);
        existing.slideIds.sort((left, right) => left - right);
      }
    } else {
      summaries.push({ name, slideIds: [slide.id] });
    }
  }

  return summaries;
}

export function resolveCourseAsset(value: string): string {
  if (value.startsWith("data:") || value.startsWith("blob:")) {
    return value;
  }

  const manifestAbsoluteUrl = new URL(MANIFEST_URL, window.location.origin);
  const resolvedUrl = new URL(value, new URL(".", manifestAbsoluteUrl));

  if (resolvedUrl.origin !== window.location.origin) {
    throw new Error("Course assets must be served from the training website.");
  }

  return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
}

function isAssetReference(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.startsWith("/") ||
    trimmed.startsWith("./") ||
    trimmed.startsWith("../") ||
    /\.(?:txt|md|vtt)(?:[?#].*)?$/i.test(trimmed)
  );
}

export async function loadCourseText(
  value: string,
  signal?: AbortSignal,
): Promise<string> {
  if (!value) {
    return "";
  }

  if (!isAssetReference(value)) {
    return value.trim();
  }

  const response = await fetch(resolveCourseAsset(value), { signal });
  if (!response.ok) {
    throw new Error(`Course text could not be loaded (${response.status}).`);
  }

  return (await response.text()).trim();
}

function parseTimestamp(value: string): number {
  const normalized = value.trim().replace(",", ".");
  const parts = normalized.split(":").map(Number);

  if (parts.some((part) => !Number.isFinite(part))) {
    return Number.NaN;
  }

  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }

  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }

  return Number.NaN;
}

export function parseWebVtt(value: string): CaptionCue[] {
  const lines = value.replace(/\r\n?/g, "\n").split("\n");
  const cues: CaptionCue[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index].trim();

    if (
      !line ||
      line === "WEBVTT" ||
      line.startsWith("NOTE") ||
      line.startsWith("STYLE") ||
      line.startsWith("REGION")
    ) {
      index += 1;
      continue;
    }

    let timingLine = line;
    if (!timingLine.includes("-->") && lines[index + 1]?.includes("-->")) {
      index += 1;
      timingLine = lines[index].trim();
    }

    if (!timingLine.includes("-->")) {
      index += 1;
      continue;
    }

    const [startValue, rawEndValue] = timingLine.split("-->");
    const endValue = rawEndValue.trim().split(/\s+/)[0];
    const start = parseTimestamp(startValue);
    const end = parseTimestamp(endValue);
    const textLines: string[] = [];

    index += 1;
    while (index < lines.length && lines[index].trim() !== "") {
      textLines.push(lines[index].trim());
      index += 1;
    }

    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      cues.push({
        start,
        end,
        text: textLines.join(" ").replace(/<[^>]*>/g, "").trim(),
      });
    }
  }

  return cues.filter((cue) => cue.text).sort((left, right) => left.start - right.start);
}

function validateManifest(value: unknown): asserts value is CourseManifest {
  if (!isRecord(value)) {
    throw new Error("The course manifest is not a valid object.");
  }

  const requiredTextFields = ["courseTitle", "candidate", "credential", "conclusion"];
  for (const field of requiredTextFields) {
    if (typeof value[field] !== "string" || value[field].trim() === "") {
      throw new Error(`The course manifest is missing ${field}.`);
    }
  }

  if (
    typeof value.totalSlides !== "number" ||
    typeof value.totalDurationMs !== "number" ||
    !Array.isArray(value.slides) ||
    !Array.isArray(value.modules) ||
    !Array.isArray(value.learningObjectives)
  ) {
    throw new Error("The course manifest is missing required course metadata.");
  }

  if (value.slides.length !== value.totalSlides) {
    throw new Error(
      `The manifest declares ${value.totalSlides} slides but contains ${value.slides.length}.`,
    );
  }

  value.slides.forEach((slide, slideIndex) => {
    if (!isRecord(slide)) {
      throw new Error(`Slide ${slideIndex + 1} is not a valid record.`);
    }

    for (const field of REQUIRED_SLIDE_FIELDS) {
      if (!(field in slide)) {
        throw new Error(`Slide ${slideIndex + 1} is missing ${field}.`);
      }
    }

    if (
      typeof slide.id !== "number" ||
      typeof slide.title !== "string" ||
      typeof slide.module !== "string" ||
      typeof slide.image !== "string" ||
      typeof slide.audio !== "string" ||
      typeof slide.transcript !== "string" ||
      typeof slide.caption !== "string" ||
      typeof slide.durationMs !== "number" ||
      typeof slide.timelineStartMs !== "number" ||
      typeof slide.timelineEndMs !== "number" ||
      typeof slide.altText !== "string"
    ) {
      throw new Error(`Slide ${slideIndex + 1} contains invalid field types.`);
    }
  });
}

export async function fetchCourseManifest(
  signal?: AbortSignal,
): Promise<CourseManifest> {
  const response = await fetch(MANIFEST_URL, { signal });

  if (!response.ok) {
    throw new Error(`The course manifest could not be loaded (${response.status}).`);
  }

  const manifest: unknown = await response.json();
  validateManifest(manifest);

  return {
    ...manifest,
    slides: [...manifest.slides].sort((left, right) => left.id - right.id),
  };
}
